import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IntakeFormTemplate } from '../../types/formBuilder';
import { getAllForms, getCaseSubmissions, getFormSubmission } from '../../firebase/formManagement';
import { DynamicIntakeFormModal } from './intake-forms/DynamicIntakeFormModal';

interface IntakeFormsSectionProps {
  caseId: string;
  intakeStatus: {
    rights?: boolean;
    emotional?: boolean;
    professional?: boolean;
  };
  userRole?: string;
  user?: any; // User object to check organizationRole
}

export function IntakeFormsSection({ caseId, intakeStatus, userRole, user }: IntakeFormsSectionProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [allForms, setAllForms] = useState<IntakeFormTemplate[]>([]);
  const [completedForms, setCompletedForms] = useState<Set<string>>(new Set());
  const [formCompletions, setFormCompletions] = useState<{ [formId: string]: number }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadForms();
  }, [caseId]);

  const calculateFormCompletion = (form: IntakeFormTemplate, answers: { [fieldName: string]: any }): number => {
    if (!form.fields || form.fields.length === 0) return 0;
    
    const totalFields = form.fields.length;
    let filledFields = 0;
    
    form.fields.forEach(field => {
      const fieldName = field.field_name || field.id;
      const value = answers[fieldName];
      
      // Check if field is filled
      if (value !== undefined && value !== null && value !== '') {
        // For array fields, check if it has at least one item
        if (Array.isArray(value)) {
          if (value.length > 0) {
            filledFields++;
          }
        } else {
          filledFields++;
        }
      }
    });
    
    return totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
  };

  const loadForms = async () => {
    try {
      setIsLoading(true);
      
      // Load all active forms
      const allForms = await getAllForms();
      console.log('Loaded forms:', allForms.length, allForms);
      
      // Filter forms based on user permissions
      let filteredForms = allForms;
      
      if (user) {
        // Get user's organization role
        const userOrgRole = user.organizationRole;
        const isSuperAdmin = user.role === 'super_admin';
        
        // Super admins can see all forms
        if (!isSuperAdmin && userOrgRole) {
          filteredForms = allForms.filter(form => {
            // If form is shared with 'all', everyone can see it
            if (form.sharedWith === 'all') {
              return true;
            }
            
            // If form is shared with 'specific', check allowedRoles
            if (form.sharedWith === 'specific') {
              // If no allowedRoles specified, allow all (backward compatibility)
              if (!form.allowedRoles || form.allowedRoles.length === 0) {
                return true;
              }
              
              // Check if user's role is in allowedRoles
              return form.allowedRoles.includes(userOrgRole);
            }
            
            // Default: allow if no sharing setting (backward compatibility)
            return true;
          });
        }
      }
      
      setAllForms(filteredForms);

      // Load case submissions to check completion status and calculate percentages
      if (caseId) {
        const submissions = await getCaseSubmissions(caseId);
        console.log('Loaded submissions for case:', caseId, submissions.length);
        
        const completedFormIds = new Set(
          submissions
            .filter(s => s.status === 'completed')
            .map(s => s.formId)
        );
        setCompletedForms(completedFormIds);
        
        // Calculate completion percentage for each form
        const completions: { [formId: string]: number } = {};
        
        for (const form of filteredForms) {
          const submission = submissions.find(s => s.formId === form.id);
          if (submission && submission.answers) {
            completions[form.id] = calculateFormCompletion(form, submission.answers);
          } else {
            // Check if there's a draft submission
            try {
              const draftSubmission = await getFormSubmission(form.id, caseId);
              if (draftSubmission && draftSubmission.answers) {
                completions[form.id] = calculateFormCompletion(form, draftSubmission.answers);
              } else {
                completions[form.id] = 0;
              }
            } catch (error) {
              completions[form.id] = 0;
            }
          }
        }
        
        setFormCompletions(completions);
      }
    } catch (error) {
      console.error('Error loading forms:', error);
      alert('Error loading forms: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const getFormIcon = (type: string) => {
    switch (type) {
      case 'rights': return '⚖️';
      case 'emotional': return '💭';
      case 'professional': return '💼';
      default: return '📄';
    }
  };

  const getFormColor = (type: string) => {
    switch (type) {
      case 'rights': return '#2ecc71';
      case 'emotional': return '#e74c3c';
      case 'professional': return '#3498db';
      default: return '#9b59b6';
    }
  };

  const handleFormClick = (form: IntakeFormTemplate) => {
    // Open form in modal
    setSelectedFormId(form.id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFormId(null);
  };

  const handleFormSuccess = () => {
    // Reload forms to update completion status
    loadForms();
  };

  if (isLoading) {
    return (
      <div className="tab-panel">
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          {t('common.loading')}...
        </div>
      </div>
    );
  }

  return (
    <div className="tab-panel">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <h3 className="form-block-title" style={{ color: '#000000', margin: 0 }}>
          📝 {t('patientDetail.intakeForm')}
        </h3>
      </div>
      
      {allForms.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
          <h4 style={{ color: '#000000', marginBottom: '12px' }}>{t('intakeForms.noFormsAvailable')}</h4>
          <p style={{ marginBottom: '20px' }}>{t('intakeForms.setupFormsFirst')}</p>
          {userRole === 'super_admin' && (
            <button
              onClick={() => navigate(`/${i18n.language}/dashboard?tab=forms`)}
              style={{
                background: '#28a745',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {t('intakeForms.goToFormsManager')}
          </button>
          )}
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
          gap: '12px', 
          width: '100%', 
          maxWidth: '1200px', 
          margin: '0 auto' 
        }}>
          {allForms.map((form) => {
            const isCompleted = completedForms.has(form.id);
            const completionPercentage = formCompletions[form.id] || 0;
            const formName = i18n.language === 'he' ? (form.nameHe || form.name) : (form.nameEn || form.name);
            const borderColor = getFormColor(form.type);
            
            // Determine completion color
            const getCompletionColor = (percentage: number) => {
              if (percentage === 100) return '#28a745';
              if (percentage >= 50) return '#ffc107';
              if (percentage > 0) return '#17a2b8';
              return '#6c757d';
            };
            
            return (
              <button
                key={form.id}
                onClick={() => handleFormClick(form)}
                style={{
                  background: '#ffffff',
                  color: '#000000',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: `2px solid ${borderColor}`,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  justifyContent: 'center',
                  aspectRatio: '1 / 1',
                  minHeight: '160px',
                  position: 'relative',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Icon with Circular Progress */}
                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100px', height: '100px' }}>
                  {/* Circular Progress Background */}
                  <svg
                    width="100"
                    height="100"
                    style={{
                      position: 'absolute',
                      transform: 'rotate(-90deg)',
                      width: '100px',
                      height: '100px'
                    }}
                  >
                    {/* Background circle */}
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="#e9ecef"
                      strokeWidth="6"
                    />
                    {/* Progress circle */}
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke={getCompletionColor(completionPercentage)}
                      strokeWidth="6"
                      strokeDasharray={`${2 * Math.PI * 45}`}
                      strokeDashoffset={`${2 * Math.PI * 45 * (1 - completionPercentage / 100)}`}
                      strokeLinecap="round"
                      style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                    />
                  </svg>
                  {/* Icon */}
                  <span style={{ fontSize: '48px', position: 'relative', zIndex: 1 }}>{getFormIcon(form.type)}</span>
                  {/* Percentage text in center */}
                  <span style={{
                    position: 'absolute',
                    bottom: '-12px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: getCompletionColor(completionPercentage),
                    backgroundColor: 'white',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap'
                  }}>
                    {completionPercentage}%
                  </span>
                </div>
                
                {/* Form Name */}
                <span style={{ 
                  fontSize: '15px',
                  lineHeight: '1.3',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  marginTop: '8px'
                }}>
                  {formName}
                </span>

                {/* Completion Badge */}
                {isCompleted && (
                  <span style={{ 
                    position: 'absolute', 
                    top: 8, 
                    left: 8,
                    fontSize: '20px'
                  }}>
                    ✅
                  </span>
                )}

                {/* Field Count Badge */}
                <span style={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  background: '#f1f3f5',
                  color: '#666',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}>
                  {form.fields.length} {t('formManager.fields')}
                </span>

                {/* Edit Button */}
                <span
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    if (caseId) handleFormClick(form); 
                  }}
                  title={t('patientDetail.edit') || 'Edit'}
                  style={{ 
                    position: 'absolute', 
                    top: 8, 
                    right: 8, 
                    background: '#f1f3f5', 
                    border: '1px solid #e9ecef', 
                    borderRadius: 6, 
                    padding: '4px 8px', 
                    cursor: 'pointer', 
                    fontSize: '14px', 
                    color: '#000000' 
                  }}
                >
                  ✏️
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Dynamic Intake Form Modal */}
      {selectedFormId && (
        <DynamicIntakeFormModal
          formId={selectedFormId}
          caseId={caseId}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
}

export default IntakeFormsSection;

